/**
 * 管理后台「充值运营」五页的接口与类型：充值活动、活动统计、充值转化、赠礼与福利、赠送兑付。
 *
 * 背景：对应 Alice 的 /recharge-campaign*、/recharge-funnel/stats、/usage-reward/*、/account-gifts*、/gift-redemption。
 *   本仓三个服务各管一部分：活动与充值转化在支付服务（/billing/admin/...），用量福利、账号赠礼与赠送兑付在控制面
 *   （/control/admin/...）。
 * 设计意图：
 *   - 不改共用的 admin.ts：这里自己发请求，路径逐字对应后端；遇到 401 先调 adminApi.me() 触发共用 http 层的
 *     会话刷新（同一时刻只刷新一次，避免并发轮换刷新令牌），成功后重试一次；失败时 me() 会通知应用回登录页。
 *   - 金额口径：订单金额是美元字符串（"12.34"），赠送与兑付是花瓣整数；本仓没有人民币与「每元糖豆」。
 * 关键约束：写操作的权限（admin 角色）由后端判断；错误统一抛 ApiError，调用方按 code 映射文案。
 */
import { adminApi } from './admin.ts'
import { ApiError, type Query } from './http.ts'

// ------------------------------------------------------------------ 充值活动

export type CampaignMode = 'none' | 'regular' | 'special'
export type BonusType = 'fixed' | 'rate' | 'combined'
export type RewardMode = 'per_order' | 'once_per_user' | 'capped_per_user'
export type StackMode = 'stack' | 'replace'
export type CampaignStatus = 'active' | 'scheduled' | 'ended' | 'disabled'

export interface CampaignTier {
  /** 充值门槛（美元，商品标价） */
  min_usd: number
  bonus_type: BonusType
  /** fixed：固定花瓣数；rate、combined：比例（百分数） */
  bonus_value: number
  /** 仅 combined：额外固定花瓣 */
  bonus_fixed?: number
  label: string
}

export interface CampaignConfig {
  mode: CampaignMode
  campaign_key: string
  campaign_name: string
  badge: string
  title: string
  description: string
  /** 适用商品（SKU）；空表示全部在售商品 */
  skus: string[]
  tiers: CampaignTier[]
  starts_at: string
  ends_at: string
  reward_mode: RewardMode
  stack_mode: StackMode
  /** 每用户固定赠送累计上限（花瓣），仅 capped_per_user 使用 */
  fixed_bonus_cap: number
}

export interface Campaign extends CampaignConfig {
  revision: number
}

export interface CampaignHistoryItem extends Campaign {
  status: CampaignStatus
  published_by: string
  published_at: string
}

export interface CampaignOverview {
  campaign: Campaign
  campaign_status: CampaignStatus
  effective_campaign: Campaign & { status: CampaignStatus }
  applied_campaigns: string[]
  history: CampaignHistoryItem[]
}

// ------------------------------------------------------------------ 活动统计

export interface CampaignStats {
  campaign_key: string
  campaign_name: string
  summary: {
    gross_paid_usd: string
    refunded_usd: string
    net_paid_usd: string
    paid_users: number
    paid_orders: number
    refunded_orders: number
    base_petals: number
    regular_bonus_petals: number
    campaign_bonus_petals: number
    total_bonus_petals: number
    total_petals: number
    campaign_value_usd: string
    campaign_bonus_rate: string
    refunded_campaign_petals: number
  }
  daily: {
    date: string
    paid_usd: string
    orders: number
    users: number
    regular_bonus_petals: number
    campaign_bonus_petals: number
    total_petals: number
  }[]
  orders: {
    paid_at: string | null
    account_id: string
    account_name: string
    account_email: string
    order_id: string
    amount_usd: string
    refund_status: 'none' | 'partial' | 'refunded'
    base_petals: number
    regular_bonus_petals: number
    campaign_bonus_petals: number
    total_bonus_petals: number
    total_petals: number
  }[]
}

// ------------------------------------------------------------------ 充值转化

export interface RetentionStat {
  users: number
  rate: string
}

export interface FunnelBreakdownRow {
  name: string
  users: number
  orders: number
  paid_usd: string
  bonus_petals: number
  paid_orders?: number
  conversion_rate?: string
}

export interface FunnelStats {
  meta: { range_days: number; timezone: string; truncated: boolean; cohort_note: string; generated_at: string }
  eligibility: { active_campaigns: number; primary_name: string | null; claimed: number }
  summary: {
    entry_exposed_users: number
    entry_clicked_users: number
    page_view_users: number
    checkout_users: number
    checkout_orders: number
    pending_orders: number
    paid_users: number
    claimed_users: number
    gross_paid_usd: string
    refunded_usd: string
    net_paid_usd: string
    bonus_petals: number
    bonus_value_usd: string
    refunded_bonus_petals: number
    entry_click_rate: string
    page_checkout_rate: string
    checkout_pay_rate: string
    exposure_claim_rate: string
    average_paid_usd: string
    closed_orders: number
  }
  funnel: { key: string; users: number }[]
  events: { counts: Record<string, number>; unique_users: Record<string, number>; failures: Record<string, number> }
  placements: { name: string; exposed_users: number; clicked_users: number; page_view_users: number; click_rate: string }[]
  sources: FunnelBreakdownRow[]
  audiences: FunnelBreakdownRow[]
  tiers: FunnelBreakdownRow[]
  /** usage_* 为 null 表示控制面暂时查不到用量，页面显示「—」而不是 0 */
  retention: {
    cohort: number
    usage_1d: RetentionStat | null
    usage_7d: RetentionStat | null
    usage_30d: RetentionStat | null
    repeat_pay_7d: RetentionStat
    repeat_pay_30d: RetentionStat
  }
  fulfillment: {
    fulfilled: number
    pending: number
    retrying: number
    refunded: number
    amount_mismatch: number
    success_rate: string
  }
  data_quality: {
    orders_missing_attribution: number
    amount_mismatch: number
    fulfillment_attention_needed: number
    healthy: boolean
  }
  risk_signals: {
    users_with_multiple_orders: number
    stale_pending_orders: number
    late_paid_after_close: number
    refunded_after_claim: number
  }
  daily: {
    date: string
    entry_exposed_users: number
    page_view_users: number
    checkout_users: number
    orders: number
    paid_users: number
    paid_usd: string
    claimed_users: number
    bonus_petals: number
  }[]
  recent_claims: {
    claimed_at: string
    account_id: string
    account_name: string
    account_email: string
    order_id: string
    audience: string
    source: string
    amount_usd: string
    bonus_petals: number
    fulfillment_status: string
    fulfillment_attempts: number
    fulfillment_error: string
  }[]
}

// ------------------------------------------------------------------ 赠礼与福利

export type RewardType = 'bonus' | 'badge'
export type GrantStatus = 'pending' | 'delivered' | 'owned' | 'failed'

export interface AccountGift {
  code: string
  label: string
  desc: string
  points: number
  default_points: number
  is_custom: boolean
}

export interface AccountGifts {
  gifts: AccountGift[]
  max_points: number
  signup: { grants: number; grants_today: number; cost_petals: number }
}

export interface RewardRuleInput {
  name: string
  enabled: boolean
  model_pattern: string
  threshold_tokens: number
  reward_type: RewardType
  reward_ref: string
  start_at: string
}

export interface RewardRule extends RewardRuleInput {
  id: number
  revision: number
  granted_count: number
  delivered_count: number
  failed_count: number
  updated_by: string
  scan_watermark: string | null
}

export interface RewardGrant {
  id: number
  rule_id: number
  rule_name: string
  account_id: string
  account_name: string
  reward_type: RewardType
  reward_ref: string
  status: GrantStatus
  attempts: number
  last_error: string
  created_at: string
  delivered_at: string | null
}

// ------------------------------------------------------------------ 赠送兑付

export interface RedemptionGroup {
  users: number
  live_users: number
  granted_petals: number
  redeemed_petals: number
  clawed_petals: number
  redeem_rate: string
  outstanding_petals: number
  live_outstanding_petals: number
  dormant_outstanding_petals: number
}

export interface GiftRedemption {
  active_days: number
  summary: RedemptionGroup & {
    dormant_users: number
    dormant_share: string
    avail_petals: number
    internal_users: number
    internal_granted_petals: number
  }
  channels: (RedemptionGroup & { channel: string })[]
  batches: (RedemptionGroup & { batch: string; channel: string; last_at: string | null })[]
  top_outstanding: {
    account_id: string
    account_name: string
    granted_petals: number
    redeemed_petals: number
    redeem_rate: string
    outstanding_petals: number
    avail_petals: number
    is_live: boolean
    last_seen: string | null
  }[]
}

// ------------------------------------------------------------------ 请求层

function failure(status: number, body: unknown): ApiError {
  const err = (body as { error?: { code?: string; message?: string } } | null)?.error
  return new ApiError(status, err?.code ?? `http_${status}`, err?.message ?? 'Request failed')
}

function url(path: string, query?: Query): string {
  if (!query) return path
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `${path}?${s}` : path
}

async function request<T>(method: string, path: string, query?: Query, body?: unknown): Promise<T> {
  const target = url(path, query)
  for (let attempt = 0; attempt < 2; attempt++) {
    let resp: Response
    try {
      resp = await fetch(target, {
        method,
        credentials: 'same-origin',
        headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      })
    } catch (e) {
      console.error('[api] 网络错误', method, target, e)
      throw new ApiError(0, 'network_error', 'Network error')
    }
    const text = await resp.text()
    let parsed: unknown
    try {
      parsed = text ? JSON.parse(text) : undefined
    } catch {
      parsed = undefined
    }
    if (resp.status === 401 && attempt === 0) {
      try {
        await adminApi.me()
      } catch (e) {
        console.warn('[api] 会话刷新失败，不再重试', e)
        throw failure(resp.status, parsed)
      }
      continue
    }
    if (!resp.ok) {
      const err = failure(resp.status, parsed)
      console.warn('[api] 请求失败', method, target, err.status, err.code)
      throw err
    }
    return parsed as T
  }
  throw new ApiError(401, 'not_logged_in', 'Login required.')
}

const B = '/billing/admin'
const C = '/control/admin'

export const campaignApi = {
  // 充值活动
  campaign: () => request<CampaignOverview>('GET', `${B}/recharge-campaign`),
  saveCampaign: (expected_revision: number, campaign: CampaignConfig) =>
    request<{ ok: boolean; campaign: Campaign }>('PUT', `${B}/recharge-campaign`, undefined, { expected_revision, campaign }),
  campaignStats: (campaign_key: string) => request<CampaignStats>('GET', `${B}/recharge-campaign/stats`, { campaign_key }),
  funnel: (days: number) => request<FunnelStats>('GET', `${B}/recharge-funnel/stats`, { days }),
  // 账号赠礼
  gifts: () => request<AccountGifts>('GET', `${C}/account-gifts`),
  setGift: (code: string, points: number | null) =>
    request<{ ok: boolean; points: number }>('PUT', `${C}/account-gifts/${encodeURIComponent(code)}`, undefined, { points }),
  // 用量福利规则与发放记录
  rules: () => request<{ rules: RewardRule[] }>('GET', `${C}/usage-reward/rules`),
  createRule: (rule: RewardRuleInput) => request<{ ok: boolean; rule: RewardRule }>('POST', `${C}/usage-reward/rules`, undefined, { rule }),
  updateRule: (id: number, expected_revision: number, rule: RewardRuleInput) =>
    request<{ ok: boolean; rule: RewardRule }>('PUT', `${C}/usage-reward/rules/${id}`, undefined, { expected_revision, rule }),
  grants: (rule_id?: string, status?: string) => request<{ grants: RewardGrant[] }>('GET', `${C}/usage-reward/grants`, { rule_id, status }),
  retryGrant: (id: number) => request<{ ok: boolean; grant: RewardGrant }>('POST', `${C}/usage-reward/grants/${id}/retry`),
  // 赠送兑付
  redemption: (active_days: number) => request<GiftRedemption>('GET', `${C}/gift-redemption`, { active_days }),
}
